// Extra bulk actions (managers): tick routines, set/clear due date, move team, quiet, repeat — one Undo per batch.
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CalendarX, BellOff, Bell } from "lucide-react";
import { workspaceApi, emitTasksChanged } from "../_shared";

const REPEATS = [["none", "One-off (not a routine)"], ["every_30m", "Every 30 min"], ["hourly", "Hourly"], ["every_2h", "Every 2 h"], ["daily", "Daily"], ["weekdays", "Weekdays"], ["weekly", "Weekly"], ["monthly", "Monthly"]];
const chip = "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] border border-white/10 text-slate-200 hover:bg-white/5 disabled:opacity-40";

export async function runBulk(ids, action, value, onDone) {
  const r = await workspaceApi.bulk(ids, action, value);
  emitTasksChanged(); onDone?.();
  toast.success(`${r.changed} updated${r.skipped ? ` · ${r.skipped} skipped (not yours to manage)` : ""}`, {
    duration: 12000, description: r.errors?.[0],
    action: { label: "Undo", onClick: async () => { await workspaceApi.bulkUndo(r.batch_id); emitTasksChanged(); onDone?.(); toast("Undone"); } },
  });
}

export default function BulkExtras({ ids, teams = [], onDone }) {
  const [busy, setBusy] = useState(false);
  const [due, setDue] = useState("");
  const go = async (a, v) => { setBusy(true); try { await runBulk(ids, a, v, onDone); } catch (e) { toast.error(e?.response?.data?.detail || "Bulk edit failed"); } finally { setBusy(false); } };
  const sel = (testid, label, opts, onPick) => (
    <select disabled={busy} defaultValue="" onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) onPick(v); }} className="ws-input max-w-[150px] text-[12px]" data-testid={testid}>
      <option value="">{label}</option>{opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
  return (
    <>
      <button disabled={busy} onClick={() => go("tick")} className={chip} data-testid="ws-bulk-tick" title="Tick routines — catches up any missed slots"><CheckCircle2 className="w-3.5 h-3.5 text-violet-300" />Tick / caught up</button>
      {sel("ws-bulk-team", "Move to team…", [["_none", "No team (personal)"], ...teams.map((t) => [t.id, t.name])], (v) => go("team", v === "_none" ? null : v))}
      <span className="inline-flex items-center gap-1">
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="ws-input w-[135px] text-[12px]" data-testid="ws-bulk-due-input" />
        <button disabled={busy || !due} onClick={() => go("due_date", due)} className={chip} data-testid="ws-bulk-due-set">Set due</button>
        <button disabled={busy} onClick={() => go("due_date", null)} className={chip} data-testid="ws-bulk-due-clear" title="Remove due dates"><CalendarX className="w-3.5 h-3.5" />Clear due</button>
      </span>
      {sel("ws-bulk-repeat", "How often…", REPEATS, (v) => go("repeat", v))}
      <button disabled={busy} onClick={() => go("quiet", true)} className={chip} data-testid="ws-bulk-quiet"><BellOff className="w-3.5 h-3.5" />Quiet</button>
      <button disabled={busy} onClick={() => go("quiet", false)} className={chip} data-testid="ws-bulk-unquiet"><Bell className="w-3.5 h-3.5" />Unquiet</button>
    </>
  );
}
